import { useState, useEffect, useRef } from "react";
import { dropDownWhite } from "../../asset";
import DataTable, { Column } from "../../components/DataTable";
import InfoCard from "../../components/InfoCard";
import { AppDispatch, RootState } from "../../store";
import { useSelector, useDispatch } from "react-redux";
import { fetchStaff, setFilters } from "../../features/staff/staffSlice";
import { AddStaffForm } from "../../types/staff";
import attendanceApis from "../../api/attendanceApis";
import { isOlderThan15Days } from "../../util/functions/UniversalFunctions";
import { toast } from "sonner";

export default function StaffAttendanceList({
  selectedDate,
  setSelectedDate,
}: {
  selectedDate: string;
  setSelectedDate: (date: string) => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const { schoolBranch } = useSelector((state: RootState) => state.access);

  const branchCode = schoolBranch?.schoolBranchCode;

  const [attendance, setAttendance] = useState<
    Record<number, "PRESENT" | "ABSENT" | "HALF_DAY">
  >({});

  const { staffList, loading, filters } = useSelector(
    (state: RootState) => state.staff,
  );

  useEffect(() => {
    if (!branchCode) return;
    dispatch(
      fetchStaff({
        ...filters,
        branchCode,
        page: 0,
        size: 40,
      }),
    );
  }, [dispatch, filters, branchCode]);

  const handleMarkAttendance = async (
    staffId: number,
    status: "PRESENT" | "ABSENT" | "HALF_DAY",
  ) => {
    if (!branchCode) return;

    if (isOlderThan15Days(selectedDate)) {
      toast.error("Attendance cannot be marked for dates older than 15 days.");
      return;
    }

    // Check if already marked
    const existing = attendance[staffId];

    if (existing === status) return; // prevent duplicate call

    const payload = {
      staffId,
      attendanceDate: selectedDate,
      status,
    };

    const res = await attendanceApis.markStaffAttendance(payload, branchCode);

    if (res?.status === 1) {
      setAttendance((prev) => ({ ...prev, [staffId]: status }));
    }
  };

  const columns: Column<AddStaffForm>[] = [
    { label: "SL NO.", accessor: "staffId" },
    {
      label: "Profile",
      accessor: "profile",
      render: (item) => (
        <div>
          {!item?.profile_image_url ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.profile_image_url}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover bg-background-200"
            />
          )}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "fullName",
      searchable: true,
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.fullName}
        </div>
      ),
    },
    {
      label: "Present",
      accessor: "present",
      render: (item) => (
        <input
          type="radio"
          name={`attendance-${item.staffId}`}
          checked={attendance[item.staffId] === "PRESENT"}
          onChange={() => handleMarkAttendance(Number(item.staffId), "PRESENT")}
          className="w-5 h-5 appearance-none border-2 border-neutral-400 rounded-full checked:bg-primary-500 checked:border-primary-500 cursor-pointer"
        />
      ),
    },
    {
      label: "Half Day",
      accessor: "HalfDay",
      render: (item) => (
        <input
          type="radio"
          name={`attendance-${item.staffId}`}
          checked={attendance[item.staffId] === "HALF_DAY"}
          onChange={() =>
            handleMarkAttendance(Number(item.staffId), "HALF_DAY")
          }
          className="w-5 h-5 appearance-none border-2 border-neutral-400 rounded-full checked:bg-success-500 checked:border-success-500 cursor-pointer"
        />
      ),
    },
    {
      label: "Absent",
      accessor: "absent",
      render: (item) => (
        <input
          type="radio"
          name={`attendance-${item.staffId}`}
          checked={attendance[item.staffId] === "ABSENT"}
          onChange={() => handleMarkAttendance(Number(item.staffId), "ABSENT")}
          className="w-5 h-5 appearance-none border-2 border-neutral-400 rounded-full checked:bg-error-500 checked:border-error-500 cursor-pointer"
        />
      ),
    },
  ];

  useEffect(() => {
    const fetchAttendanceForDate = async () => {
      if (!branchCode || !selectedDate) return;

      const res = await attendanceApis.getStaffDayWiseAttendance(
        branchCode,
        selectedDate,
      );

      if (!res?.data) return;

      const attendanceMap: Record<number, "PRESENT" | "ABSENT" | "HALF_DAY"> =
        {};

      res.data.forEach((record) => {
        if (record.status) {
          attendanceMap[record.staffId] = record.status;
        }
      });

      setAttendance(attendanceMap);
    };

    fetchAttendanceForDate();
  }, [selectedDate, branchCode]);

  const presentCount = Object.values(attendance).filter(
    (status) => status === "PRESENT",
  ).length;

  return (
    <div className="grid gap-3">
      <div className="grid md:grid-cols-3 gap-5 bg-white rounded-lg py-4 px-5">
        <InfoCard
          label="Academic Year"
          value="2026-27"
          bgColor="bg-primary-50"
          textColor="text-primary-700"
        />
        <InfoCard label="Present Today" value={presentCount.toString()} />

        <InfoCard
          label="Total Number of Staffs"
          value={staffList.length.toString()}
          bgColor="bg-error-50"
          textColor="text-error-700"
        />
      </div>

      <div className="grid gap-1">
        <div className="bg-white rounded-lg py-2 items-center px-4 flex gap-7 w-fit">
          <label className="text-neutral-400 text-sm font-semibold">Date</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => dateInputRef.current?.showPicker()}
              className="bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-3 hover:bg-primary-600 transition-colors"
            >
              {selectedDate.split("-").reverse().join("/")}
              <img src={dropDownWhite} alt="dropdown" className="" />
            </button>

            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="absolute opacity-0 pointer-events-none left-0 bottom-0 w-0 h-0"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg py-2 items-center px-4 flex gap-7 overflow-x-auto">
          <DataTable
            columns={columns}
            data={staffList}
            isLoading={loading}
            onSearchChange={(acc, val) =>
              dispatch(setFilters({ [acc]: val || "", page: 0 }))
            }
          />
        </div>
      </div>
    </div>
  );
}
